<script lang="ts">
  import { onMount } from 'svelte'
  import { Icon, icons, Notification } from '@factry/components'
  import Header from '../components/Header.svelte'
  import ESG from '../tabs/ESG.svelte'
  import Overview from '../tabs/Overview.svelte'
  import Quality from '../tabs/Quality.svelte'
  import client from '../utils/client'
  import { getLineDatasets } from '../utils/datasets'
  import mockEvents from '../utils/mock_events.json'
  import mockMeasurements from '../utils/mock_measurements.json'
  import type { AssetEvent, Dataset, MeasurementRecord, TabTypes } from '../types'

  export let test: string | undefined
  export let uuid: string | undefined

  const tabs: TabTypes[] = ['Overview', 'Quality', 'ESG Metrics']

  let activeTab: TabTypes = 'Overview'
  let datasets: Dataset[] = []
  let event: AssetEvent | undefined
  let measurements: MeasurementRecord[] = []

  async function getData (): Promise<void> {
    if (test !== 'true' && (typeof uuid !== 'string' || !uuid.length)) return

    try {
      let events: AssetEvent[] = []
      // The current data isn't great, so if the test param is set to true, use a mock event.
      if (test === 'true') {
        events = mockEvents
      } else {
        events = await client('/dpp/events', { params: { rowKey: uuid } })
        if (!Array.isArray(events) || !events.length) throw new Error('Could not load events')
      }

      // We filtered for 1 event, so only use the first item of the array
      event = events[0]
      if (!event?.startTime || !event?.stopTime) throw new Error('Event doesn\'t have a start and stop time')

      let dirtyMeasurements: MeasurementRecord[]
      // The current data isn't great, so if the test param is set to true, use some mock measurements.
      if (test === 'true') {
        dirtyMeasurements = mockMeasurements as MeasurementRecord[]
      } else {
        // Load measurements by the start and end of the event.
        // Sometimes the values are returned as strings, so consider these measurements dirty.
        dirtyMeasurements = await client('/dpp/table', {
          params: {
            start: event?.startTime,
            end: event?.stopTime,
          },
        })
      }

      measurements = dirtyMeasurements
        // Get rid of measurements without values or valid timestamps
        .filter(m =>
          (typeof m.value === 'string' || typeof m.value === 'number') &&
          (m.Timestamp || m.ts) &&
          !isNaN(Date.parse(m.Timestamp || m.ts))
        )
        // Values are sometimes saved as strings
        .map(dm => {
          return (typeof dm.value === 'string')
            ? { ...dm, value: parseFloat(dm.value) }
            : dm
        })
        // Even if we parsed the value to make strings work, it might have returned NaN.
        .filter(m => typeof m.value === 'number' && !isNaN(m.value))

      // Construct the datasets here so we can use them for the graph and the min-max components.
      datasets = getLineDatasets(measurements)
    } catch (err) {
      Notification.error('Could not load all event data', err)
    }
  }

  onMount(() => { void getData() })
</script>

<style lang="scss">
  @import '../utils/base';

  .wrapper {
    background-color: $white-ter;
    min-height: 100dvh;
  }

  .block-info {
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: $spacing * 5;
  }

  summary {
    background-color: $white;
    border-bottom: 1px solid $border;
    padding: $spacing * 0.5;

    .title {
      margin-bottom: $spacing * 0.25;
    }

    p {
      color: $grey;
      display: flex;
      flex-wrap: wrap;
    }

    span {
      align-items: center;
      display: flex;
      margin-right: $spacing;

      & ~ span:not(:empty)::before {
        background: $grey;
        border-radius: 100%;
        content: "";
        flex-shrink: 0;
        height: $spacing * 0.25;
        margin-right: $spacing;
        width: $spacing * 0.25;
      }
    }
  }

  .tabs-wrapper {
    padding: $spacing * 0.5;
  }

  .tabs {
    background-color: $grey-lightest;
    border-radius: 100vw;
    padding: ($spacing * 0.125) ($spacing * 0.03);

    ul {
      justify-content: center;
    }

    li {
      padding: 0 ($spacing * 0.125);
      max-width: 350px;

      a {
        border-radius: 100vw;
        border: 0;
        padding: $spacing * 0.125;
        transition: all $speed * 3 ease-in-out;

        &:hover {
          background-color: $white;
          color: $black;
        }
      }

      &.is-active a {
        background-color: $white;
        color: $black;
      }
    }
  }
</style>

<div class="wrapper">
  <Header />

  <main>
    <summary>
      {#if event}
        <h1 class="title is-2">Certificate</h1>
        <p>
          <span>Recipe: {event.recipe}</span>
          <span>Length: {event?.length || '12.2 m'}</span>
          <span>Wall Thickness: {event?.wall_thickness || '25.4 mm'}</span>
          <span>Design Life: {event?.design_life || '30 years'}</span>
        </p>
        <p>Complete quality and ESG passport for this pipeline section</p>
      {:else}
        <div class="block-info">
          <Icon icon={icons.loading} />
          <span>Loading</span>
        </div>
      {/if}
    </summary>

    {#if event}
      <div class="tabs-wrapper">
        <div class="tabs is-toggle is-fullwidth">
          <ul>
            {#each tabs as tab}
              <!-- svelte-ignore a11y-missing-attribute -->
              <li
                class:is-active={tab === activeTab}
                on:click={() => (activeTab = tab)}
                on:keypress={(e) => e.key === 'Enter' && (activeTab = tab)}
              >
                <a>{tab}</a>
              </li>
            {/each}
          </ul>
        </div>
        <div class="content">
          {#if activeTab === 'Overview'}
            <Overview {event} />
          {/if}
          {#if activeTab === 'Quality'}
            <Quality {datasets} {event} />
          {/if}
          {#if activeTab === 'ESG Metrics'}
            <ESG {event} />
          {/if}
        </div>
      </div>
    {/if}
  </main>
</div>
